/** The console's permission hooks.
 *
 * One request answers the whole console: `GET /api/agent/me/` returns the
 * effective permission set, and every screen gates on that rather than asking
 * per button. `lib/authz` holds the pure helpers; this is the React binding.
 *
 * ## Scope
 *
 * The organisation scope control is a filter, not a tenant switch (see
 * `scope.tsx`), but it does change the answer: modules are licensed per tenant,
 * so `change.approve` can be usable in one organisation and not in another. The
 * query key carries the scope for that reason.
 *
 * When no scope is chosen — the default — the key matches the one `AgentLayout`
 * uses, so the console still makes exactly one request for this.
 */
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useMemo } from 'react'

import {
  type AgentMe,
  type ModuleKey,
  type Permission,
  can,
  canAll,
  canAny,
  canOnPlatform,
  isPlatformUser,
  moduleEnabled,
} from '../lib/authz'
import { agentApi } from './api'
import { useOrgScope } from './scope'

/** The query key and fetcher, shared so every caller hits one cache entry. */
export function meQuery(orgId: string | null) {
  return {
    queryKey: ['agent-me', orgId] as const,
    queryFn: () =>
      agentApi.get<AgentMe>(orgId ? `/me/?organization=${encodeURIComponent(orgId)}` : '/me/'),
  }
}

export interface Authz {
  me: AgentMe | undefined
  isLoading: boolean
  can: (permission: Permission) => boolean
  canAny: (permissions: Permission[]) => boolean
  canAll: (permissions: Permission[]) => boolean
  canOnPlatform: (permission: Permission) => boolean
  moduleEnabled: (module: ModuleKey) => boolean
  isPlatformUser: boolean
}

/** What every screen uses to decide what to render. */
export function useAuthz(): Authz {
  const { orgId } = useOrgScope()
  const { data: me, isLoading } = useQuery({ ...meQuery(orgId), staleTime: 60_000 })

  return useMemo(
    () => ({
      me,
      isLoading,
      can: (permission: Permission) => can(me, permission),
      canAny: (permissions: Permission[]) => canAny(me, permissions),
      canAll: (permissions: Permission[]) => canAll(me, permissions),
      canOnPlatform: (permission: Permission) => canOnPlatform(me, permission),
      moduleEnabled: (module: ModuleKey) => moduleEnabled(me, module),
      isPlatformUser: isPlatformUser(me),
    }),
    [me, isLoading],
  )
}

/** Convenience for the common case — one permission, one decision. */
export function usePermission(permission: Permission): boolean {
  return useAuthz().can(permission)
}

interface CanProps {
  /** Held in the organisation in scope. */
  permission?: Permission
  /** Any one of these is enough. */
  anyOf?: Permission[]
  /** The optional module this depends on, if any. */
  module?: ModuleKey
  /** Rendered instead when the check fails. Nothing, by default. */
  fallback?: ReactNode
  children: ReactNode
}

/** Renders its children only if the caller may actually do the thing.
 *
 *  A wrapper rather than an inline `&&` so the reason a control is missing is
 *  greppable: `<Can permission="ticket.assign">` says why, where a boolean does
 *  not. Renders nothing while the payload is loading — fail closed, so a control
 *  never flashes into view and then vanishes.
 */
export function Can({ permission, anyOf, module, fallback = null, children }: CanProps) {
  const authz = useAuthz()

  if (module && !authz.moduleEnabled(module)) return <>{fallback}</>
  if (permission && !authz.can(permission)) return <>{fallback}</>
  if (anyOf && !authz.canAny(anyOf)) return <>{fallback}</>

  return <>{children}</>
}
